import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './Button.module.css';

// Button — DESIGN_SYSTEM §12 row 1 (`.btn`). Verbs, not nouns ("Stage change", "Review 3 changes").
// States: default · hover · pressed · focus · loading · disabled. Loading keeps the button focusable
// (`aria-disabled` + `aria-busy`) so focus does not fall off the page while the caller's "-ing…" label shows.

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonStyleProps {
  /** `secondary` by default: a view has one primary action and the caller names it. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full-width (mobile-width forms, modal footers). */
  block?: boolean;
}

interface ButtonContentProps extends ButtonStyleProps {
  /** Lucide icon left of the label. */
  icon?: IconName;
  /** Chevron (or another small icon) right of the label — menus, date control. */
  trailingIcon?: IconName;
  /** Icon-only buttons need an `aria-label` (§8: "an icon-only button always has an aria-label"). */
  iconOnly?: boolean;
  testID?: string;
  children?: ReactNode;
}

export interface ButtonProps
  extends ButtonContentProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Shows the spinner, sets `aria-busy` and ignores clicks; pass the "-ing…" label as children. */
  loading?: boolean;
}

const VARIANT_CLASS: Record<ButtonVariant, string | undefined> = {
  primary: styles.primary,
  secondary: styles.secondary,
  ghost: styles.ghost,
  danger: styles.danger,
  'danger-ghost': styles.dangerGhost,
};

export function buttonClassName({
  variant = 'secondary',
  size = 'md',
  block = false,
  iconOnly = false,
  loading = false,
  className,
}: ButtonStyleProps & { iconOnly?: boolean; loading?: boolean; className?: string }): string {
  return cx(
    styles.btn,
    VARIANT_CLASS[variant],
    size === 'sm' && styles.sm,
    size === 'lg' && styles.lg,
    block && styles.block,
    iconOnly && styles.iconOnly,
    loading && styles.loading,
    className,
  );
}

function assertIconOnlyHasName(iconOnly: boolean, props: { 'aria-label'?: string }) {
  if (import.meta.env.DEV && iconOnly && !props['aria-label']) {
    throw new Error('An icon-only Button needs an aria-label (DESIGN_SYSTEM §8).');
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size,
    block,
    icon,
    trailingIcon,
    iconOnly = false,
    loading = false,
    disabled,
    testID,
    className,
    children,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  assertIconOnlyHasName(iconOnly, rest);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClassName({ variant, size, block, iconOnly, loading, className })}
      disabled={disabled}
      aria-disabled={loading ? true : undefined}
      aria-busy={loading ? true : undefined}
      data-testid={testID}
      onClick={handleClick}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {icon && !loading ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} className={styles.trailing} /> : null}
    </button>
  );
});

export interface ExternalLinkButtonProps
  extends ButtonContentProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  /** An absolute URL that LEAVES the app — Meta's own tools. Never carries an identifier (REQ-002 AC3). */
  href: string;
}

/**
 * A link out of AdOrchestrator that looks like a button — "Open Ads Manager", "Create in Ads Manager". It is not
 * a router `Link` (the destination is not ours) and it always opens in a new tab with `rel="noreferrer
 * noopener"`, so the page we hand the user to learns nothing about where they came from.
 */
export function ExternalLinkButton({
  variant,
  size,
  block,
  icon,
  trailingIcon,
  iconOnly = false,
  testID,
  className,
  children,
  ...rest
}: ExternalLinkButtonProps) {
  assertIconOnlyHasName(iconOnly, rest);
  return (
    <a
      className={buttonClassName({ variant, size, block, iconOnly, className })}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={testID}
      {...rest}
    >
      {icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} className={styles.trailing} /> : null}
    </a>
  );
}

export interface LinkButtonProps extends ButtonContentProps, Omit<LinkProps, 'children'> {}

/** A router link that looks like a button (the welcome CTA, "Open in Ads Manager"). */
export function LinkButton({
  variant,
  size,
  block,
  icon,
  trailingIcon,
  iconOnly = false,
  testID,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  assertIconOnlyHasName(iconOnly, rest);
  return (
    <Link
      className={buttonClassName({ variant, size, block, iconOnly, className })}
      data-testid={testID}
      {...rest}
    >
      {icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} className={styles.trailing} /> : null}
    </Link>
  );
}
